// Vehicle Dashboard "Video" card content for dashcam vehicles (device_type=dashcam
// + has_video). Left: live channel launcher reusing LiveTrackingVideoModal with
// auto-start. Right: 3 latest event clips reusing VideoPlayerModal + play-url polling.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Camera, ExternalLink, Film, Loader2, Play, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { LiveTrackingVideoModal } from '../live-tracking/LiveTrackingVideoModal';
import { VideoPlayerModal } from '../media/VideoPlayerModal';
import { getCameraRecordingState, getConfiguredCameraCount } from '../../utils/cameraRecordingState';
import { formatDT } from '../../utils/timezone';

const stripEventCodeSuffix = (label) => String(label || '').replace(/\s*\(\d{1,5}\)\s*$/u, '').trim();

export const VehicleVideoCardContent = ({ device, showLive, showClips }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [clips, setClips] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [liveModal, setLiveModal] = useState({ open: false, channel: null });
  const [player, setPlayer] = useState({ open: false, video: null, url: null });
  const pollTimerRef = useRef(null);

  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  useEffect(() => {
    if (!showClips || !device?.id) return;
    let cancelled = false;
    setClipsLoading(true);
    api.get('/media/event-videos', { params: { device_id: device.id, limit: 3 } })
      .then((res) => { if (!cancelled) setClips(res.data?.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setClipsLoading(false); });
    return () => { cancelled = true; };
  }, [api, device?.id, showClips]);

  const pollPlayUrl = useCallback(async (video, attempt = 0) => {
    try {
      const res = await api.get(`/media/event-videos/${video.id}/play-url`);
      if (res.data?.url) {
        setPlayer((prev) => (prev.video?.id === video.id ? { ...prev, url: res.data.url } : prev));
        return;
      }
      const status = res.data?.playback_status || res.data?.status;
      if (['queued', 'converting'].includes(status) && attempt < 60) {
        if (attempt === 0) toast.info('Preparing video for playback…');
        const retryMs = Math.max(3, Number(res.data?.retry_after || 5)) * 1000;
        pollTimerRef.current = setTimeout(() => pollPlayUrl(video, attempt + 1), retryMs);
        return;
      }
      throw new Error(res.data?.message || 'Video playback unavailable');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Failed to prepare event video');
    }
  }, [api]);

  const playClip = (video) => {
    const filename = `${video.video_filename || video.filename || video.s3_key || ''}`.toLowerCase();
    if (video.media_kind === 'snapshot' || /\.(jpe?g|png|webp)$/.test(filename)) {
      toast.info('Snapshot images are used as video thumbnails');
      return;
    }
    const normalised = { ...video, video_id: video.id, filename: video.video_filename || video.filename };
    setPlayer({ open: true, video: normalised, url: null });
    if (video.s3_key) {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
      pollPlayUrl(video, 0);
    }
  };

  const channelCount = getConfiguredCameraCount(device);
  const recState = getCameraRecordingState(device);
  const channels = Array.from({ length: channelCount }, (_, i) => i + 1);

  return (
    <>
      <div className={`grid gap-4 ${showLive && showClips ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
        {showLive && (
          <div data-testid="vehicle-video-live-block">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 text-red-500" /> Live Cameras
            </p>
            <div className="grid grid-cols-2 gap-3">
              {channels.map((ch) => {
                const label = device.channel_names?.[String(ch)] || `CH${ch}`;
                const recording = recState.isGprsOnline && recState.recordingByChannel[ch];
                const unavailable = !recState.isGprsOnline || !recording;
                return (
                  <button
                    key={ch}
                    type="button"
                    disabled={unavailable}
                    onClick={() => setLiveModal({ open: true, channel: ch })}
                    title={!recState.isGprsOnline ? 'Device offline — streaming disabled' : (!recording ? `${label} is not recording — streaming disabled` : `Watch ${label} live`)}
                    className={`group relative aspect-video overflow-hidden rounded-lg border bg-gradient-to-br from-slate-800 to-slate-950 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 ${unavailable ? 'cursor-not-allowed opacity-50' : 'transition-transform hover:scale-[1.02]'}`}
                    data-testid={`vehicle-video-channel-${ch}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Camera className={`w-7 h-7 text-slate-500 ${unavailable ? '' : 'transition-opacity group-hover:opacity-0'}`} />
                      {!unavailable && <Play className="absolute w-8 h-8 text-white opacity-0 drop-shadow transition-opacity group-hover:opacity-100" />}
                    </div>
                    <span className={`absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${recording ? 'bg-red-600/90 text-white' : 'bg-slate-700/80 text-slate-300'}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${recording ? 'bg-white animate-pulse' : 'bg-slate-400'}`} />
                      {recording ? 'REC' : 'OFF'}
                    </span>
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2 pb-1.5 pt-4 text-xs font-medium text-white">{label}</span>
                  </button>
                );
              })}
            </div>
            <Button
              size="sm"
              className="mt-3 h-8 bg-red-600 hover:bg-red-700 text-white gap-1.5 disabled:opacity-50"
              disabled={!recState.hasAnyRecording}
              title={recState.hasAnyRecording ? 'Open live video' : 'Video module is not ON — live video unavailable'}
              onClick={() => setLiveModal({ open: true, channel: null })}
              data-testid="vehicle-video-open-live-btn"
            >
              <Play className="w-3.5 h-3.5" /> Open Live Video
            </Button>
            {!recState.hasAnyRecording && (
              <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="vehicle-video-module-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  {recState.isGprsOnline
                    ? 'Video module is not ON — usually the ignition is off. Live video is unavailable until the cameras start recording.'
                    : 'Video module is not ON — device is offline (no GPRS). Live video is unavailable until the device reconnects.'}
                </span>
              </div>
            )}
            <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground" data-testid="vehicle-video-status-line">
              <span className="inline-flex items-center gap-1">
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${recState.isGprsOnline ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                {recState.isGprsOnline ? 'Device online' : 'Device offline'}
              </span>
              <span>·</span>
              <span>{channelCount} camera{channelCount === 1 ? '' : 's'}</span>
              {recState.isGprsOnline && (
                <>
                  <span>·</span>
                  <span>{channels.filter((ch) => recState.recordingByChannel[ch]).length} recording</span>
                </>
              )}
            </p>
          </div>
        )}
        {showClips && (
          <div data-testid="vehicle-video-clips-block">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Film className="w-3.5 h-3.5 text-[#2563eb]" /> Recent Event Clips
              </p>
              <button
                type="button"
                className="text-xs font-medium text-[#2563eb] hover:underline inline-flex items-center gap-1"
                onClick={() => navigate(`/media?tab=events&device_id=${device.id}`)}
                data-testid="vehicle-video-view-all-link"
              >
                View all <ExternalLink className="w-3 h-3" />
              </button>
            </div>
            {clipsLoading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading clips…</div>
            ) : clips.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground" data-testid="vehicle-video-clips-empty">
                No event videos yet
              </div>
            ) : (
              <div className="space-y-2">
                {clips.map((clip) => {
                  const notUploaded = clip.media_kind !== 'snapshot' && !clip.s3_key;
                  return (
                  <button
                    key={clip.id}
                    type="button"
                    disabled={notUploaded}
                    title={notUploaded ? 'Not uploaded from device yet' : undefined}
                    onClick={() => playClip(clip)}
                    className={`flex w-full items-center gap-3 rounded-lg border p-2 text-left ${notUploaded ? 'cursor-not-allowed opacity-50' : 'transition-colors hover:bg-muted/60'}`}
                    data-testid={`vehicle-video-clip-${clip.id}`}
                  >
                    <div className="relative h-11 w-[72px] shrink-0 overflow-hidden rounded bg-muted">
                      {clip.thumbnail_url ? (
                        <img src={clip.thumbnail_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center"><Camera className="w-4 h-4 text-muted-foreground" /></div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                        <Play className="w-4 h-4 text-white drop-shadow" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">
                        {stripEventCodeSuffix(clip.event_label_display || clip.event_label) || clip.event_type || 'Event clip'}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {(device.channel_names?.[String(clip.channel)] || `CH${clip.channel ?? '?'}`)} · {formatDT(clip.created_at)}{notUploaded ? ' · Not uploaded yet' : ''}
                      </p>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <LiveTrackingVideoModal
        open={liveModal.open}
        device={device}
        autoStartChannel={liveModal.channel}
        onOpenChange={(next) => { if (!next) setLiveModal({ open: false, channel: null }); }}
      />
      <VideoPlayerModal
        open={player.open}
        onClose={() => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); setPlayer({ open: false, video: null, url: null }); }}
        video={player.video}
        videoUrl={player.url}
        device={device}
      />
    </>
  );
};

export default VehicleVideoCardContent;
